import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import api from '@/api';
import type { components } from '@/api/qraft/schema';
import { NotFoundScreen } from '@/common/ErrorScreen';
import { FollowUpNotes } from '@/follow-up/FollowUpNotes';
import { SectionHeading } from '@/follow-up/followUpStyles';
import {
  GroupFollowUpRequestDetail,
  Section,
} from '@/follow-up/GroupFollowUpRequestDetail';
import { formatLongDate } from '~/utils/dateFormat';
import { formatString } from '~/utils/stringFormat';

type RequestDetail = components['schemas']['GroupFollowUpRequestDetail'];

export const Route = createFileRoute(
  '/_app/_dashboard/groups/$groupName/follow-ups/follow-up-requests/view/$requestId',
)({
  component: ViewGroupRequestScreen,
});

/**
 * Subnav 1's request detail — `buddypress/groups/single/study-group/follow-ups/
 * follow-up-requests/view.php`, all 103 lines of it.
 *
 * Three of its blocks are deliberately absent:
 *
 *   * the *See the follow-up for this request* banner (`:31-38`) — 0 of 137
 *     migrated rows are `approved` with a follow-up, its source column
 *     (`follow_up_id` on the request) has no v3 counterpart, and its link target
 *     `follow-ups/follow-ups/view/{id}` is an unregistered slug that silently
 *     renders the default subnav;
 *   * the `if` arm of `:53-67` (*Message to Study Helper*), which needs
 *     `creator_id == student_id` — 0 of 137 after D36, and structurally
 *     impossible since `follow_up_requests.creator_id` FKs `study_helpers.id`;
 *   * the `if` arm of `:80-84`, `message_for_student_reject`, which is `''` on
 *     137 of 137 and belongs to a viewer who is the student.
 *
 * Everything else renders, including the empty-value fallbacks: this template
 * is `echo wpautop(stripslashes($col)) ?: __(…)` on ALL FOUR of its message
 * blocks, and without them 13 of 34 approved, 20 of 27 rejected and 17 of 18
 * canceled rows render an empty box.
 */
function ViewGroupRequestScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { groupName, requestId } = Route.useParams();

  const { data: groupData } = api.getGroupBySlug.useQuery({
    path: { group_slug: groupName },
  });
  const groupId = groupData?.id;

  const {
    data: detail,
    isLoading,
    isError,
  } = api.getGroupFollowUpRequest.useQuery(
    {
      path: { group_id: groupId ?? 0, request_id: Number(requestId) },
    },
    { enabled: groupId !== undefined },
  );

  const backToSubnav = () =>
    void navigate({
      to: '/groups/$groupName/follow-ups/follow-up-requests/',
      params: { groupName },
    });

  // `getGroupFollowUpRequest` answers 404 for a request outside the group's
  // derived roster (DIV-4), which is the same screen an unknown id gets.
  if (isError) return <NotFoundScreen />;
  if (isLoading || detail === undefined) return null;

  return (
    <GroupFollowUpRequestDetail
      detail={detail}
      onBack={backToSubnav}
      beforeContact={<StudyInfoBlock detail={detail} />}
    >
      {/* `:60-67`, the `else` arm — unconditional on this surface after D36, and
          non-empty on 106 of 137 group-reachable requests. Legacy prints
          'No message' rather than dropping the block when it is empty. */}
      <FollowUpNotes
        title={t('lessons.message-from-study-helper')}
        message={detail.message_for_student || t('wbs.no-message-2')}
      />
      <StatusBlock detail={detail} />
    </GroupFollowUpRequestDetail>
  );
}

/**
 * `buddypress/follow-ups/follow-up-requests/student-study-info.php`, served from
 * the request's stored SNAPSHOT.
 *
 * Legacy json_decodes the snapshot on line 1 and immediately overwrites it from
 * `wbs_get_user_study_progress()` on line 2, so its detail and its own datatable
 * columns disagree on the same data. DIV-9 keeps the snapshot, which is wave
 * rule 5 and what makes the list and the detail agree.
 */
function StudyInfoBlock({ detail }: { detail: RequestDetail }) {
  const { t } = useTranslation();
  const progress = detail.study_progress;

  // `student-study-info.php:7` is `$graduated ? Yes : No` through PHP
  // truthiness, and the stored value is a STRING — `''` on 136 of 137 migrated
  // rows and `'1'` on 1. `'0'` is excluded explicitly because PHP would call it
  // falsy and JavaScript would not.
  const graduated = progress.graduated !== '' && progress.graduated !== '0';

  return (
    <Section>
      <SectionHeading>{t('lessons.student-s-study-info')}</SectionHeading>
      <StudyGrid>
        <Field>
          <FieldLabel>{t('wbs.graduated-2')}</FieldLabel>
          <span>{graduated ? t('wbs.yes') : t('wbs.no')}</span>
        </Field>
        <Field>
          <FieldLabel>{t('wbs.current-progress')}</FieldLabel>
          {/* `study_progress_label` is the snapshot's `current_progress`
              LearnDash post id resolved to a title; `''` is legacy's
              `! empty($last_id)` else-branch.

              DIVERGENCE — legacy prints `no progress` there
              (`student-study-info.php:15`, catalogue unit 43098, EN + PT_BR
              only). This group's locale delta was closed at ten keys with that
              string deliberately not minted (spec §3.5), so the existing 4/4
              `n/a` stands in. One word of English copy; flipping it is one
              key. */}
          <span>{detail.study_progress_label || t('wbs.n-a-2')}</span>
        </Field>
        <Field>
          <FieldLabel>{t('lessons.average-quiz-score')}</FieldLabel>
          <span>{progress.average_quiz_score}%</span>
        </Field>
        <Field>
          <FieldLabel>{t('lessons.completed-self-paced-courses')}</FieldLabel>
          <span>{progress.completed_self_paced_courses}</span>
        </Field>
      </StudyGrid>
    </Section>
  );
}

/**
 * The one status-branched block at `view.php:69-98`. `pending`, `adopted` and
 * `in_progress` render no final block at all — there is no `else`.
 */
function StatusBlock({ detail }: { detail: RequestDetail }) {
  const { t } = useTranslation();

  if (detail.status === 'approved') {
    return (
      <FollowUpNotes
        // `:71` — 'Your note to the Follow-up Coordinator WHEN Accepted'
        // (unit 43094). The pending detail's heading is the near-identical
        // 'if Accepted' (unit 22806) and they are different catalogue strings.
        title={t('wbs.your-note-to-the-4')}
        message={
          detail.message_for_follow_up_coordinator || t('wbs.no-message-2')
        }
      />
    );
  }

  if (detail.status === 'rejected') {
    return (
      <FollowUpNotes
        // 'Feedback on for Rejection' (sic) — legacy's own copy, `:78`.
        title={t('wbs.feedback-on-for-rejection')}
        message={
          detail.message_for_study_helper_reject || t('wbs.no-message-2')
        }
      />
    );
  }

  if (detail.status === 'canceled') {
    return (
      <FollowUpNotes
        title={
          <>
            {t('wbs.reason-for-cancelation')}
            {/* `:91` prints `wbs_date($canceled_at)` unconditionally inside the
                H4; `canceled_at` is NULL on 17 of 18 migrated canceled rows
                (4 legacy survivors + 14 W4.1 dedupe demotions), so the
                parenthetical is rendered only when there is a timestamp. */}
            {detail.canceled_at !== null && (
              <CanceledOn>
                {formatString(
                  t('wbs.cancelled-on-s'),
                  formatLongDate(detail.canceled_at),
                )}
              </CanceledOn>
            )}
          </>
        }
        // `:95` is `?: __('n/a')`, a DIFFERENT fallback from the other three
        // blocks on this template. Legacy's, not a slip.
        message={detail.message_for_cancelation || t('wbs.n-a-2')}
      />
    );
  }

  return null;
}

const StudyGrid = styled.div`
  column-gap: 25px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  row-gap: 22px;

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.div`
  display: grid;
  font-size: 13px;
  gap: 16px;
  grid-template-columns: 190px 1fr;
`;

const FieldLabel = styled.span`
  color: var(--grey-900);
`;

const CanceledOn = styled.small`
  font-size: 13px;
  margin-left: 5px;
`;
